import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { MESSAGE_MAX_LENGTH, type GiftVoucherType } from '@/types/gift-voucher';

export interface ValueVoucherDraft {
  type: 'value';
  valueRon: number;
}

export interface PackageVoucherDraft {
  type: 'package';
  roomTypeId: string;
  roomTypeName: string;
  nights: number;
  withSpa: boolean;
  sparklingWine: boolean;
  sparklingNote: string;
  restaurantCreditRon: number;
  spaServices: Array<{ key: string; count: number; unitPriceRon: number }>;
  /** Server-provided base/with-spa nightly RON prices captured when the user picked the room. */
  basePriceRon: number;
  withSpaPriceRon: number;
}

export interface SpaPassVoucherDraft {
  type: 'spa_pass';
  entries: 10 | 15;
  valueRon: number;
}

export type VoucherDraft = ValueVoucherDraft | PackageVoucherDraft | SpaPassVoucherDraft;

interface GiftVoucherStore {
  draft: VoucherDraft | null;
  beneficiaryName: string;
  personalizedMessage: string;
  setDraft: (draft: VoucherDraft | null) => void;
  setBeneficiary: (name: string) => void;
  setMessage: (message: string) => void;
  clear: () => void;
}

export const useGiftVoucherStore = create<GiftVoucherStore>()(
  persist(
    (set) => ({
      draft: null,
      beneficiaryName: '',
      personalizedMessage: '',
      setDraft: (draft) => set({ draft }),
      setBeneficiary: (name) => set({ beneficiaryName: name }),
      setMessage: (message) => set({ personalizedMessage: message.slice(0, MESSAGE_MAX_LENGTH) }),
      clear: () => set({ draft: null, beneficiaryName: '', personalizedMessage: '' }),
    }),
    {
      name: 'gift-voucher-draft',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/** Compute total RON for any draft. */
export function draftTotalRon(draft: VoucherDraft | null): number {
  if (!draft) return 0;
  if (draft.type === 'value') return draft.valueRon;
  if (draft.type === 'spa_pass') return draft.valueRon;
  // package
  const nightly = draft.withSpa ? draft.withSpaPriceRon : draft.basePriceRon;
  const accommodation = nightly * draft.nights;
  const sparkling = draft.sparklingWine ? PACKAGE_SPARKLING_WINE_PRICE_RON : 0;
  const services = draft.spaServices.reduce((sum, s) => sum + s.count * s.unitPriceRon, 0);
  return accommodation + draft.restaurantCreditRon + sparkling + services;
}

/** Shared price constants used by both the package form and the totals helper. */
export const PACKAGE_SPARKLING_WINE_PRICE_RON = 150;
export const PACKAGE_RESTAURANT_MIN_RON = 200;
export const PACKAGE_VOUCHER_MIN_NIGHTS = 1;
export const VALUE_VOUCHER_MIN_RON = 700;
export const VALUE_VOUCHER_DEFAULT_RON = 1000;
export const SPA_PASS_PRICE_RON: Record<10 | 15, number> = { 10: 500, 15: 750 };
export const PACKAGE_SPA_SERVICES: Array<{ key: string; unitPriceRon: number }> = [
  { key: 'warmHealing', unitPriceRon: 240 },
  { key: 'slowDown', unitPriceRon: 190 },
  { key: 'reinvigorating', unitPriceRon: 139 },
];

export const VOUCHER_TYPE_LABEL_KEY: Record<GiftVoucherType, string> = {
  value: 'flexible.title',
  package: 'curated.title',
  spa_pass: 'spa.title',
};
